/**
 * Allowed Sender Addresses — mirrors iOS AllowedSenderAddressesView.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Input, List, Spin, message } from 'antd';
import {
  ArrowLeftOutlined,
  DeleteOutlined,
  MailOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import { settingsEn } from '../../content/settings.en';
import {
  type InboundAlternateSender,
  InboundEmailNotProvisionedError,
  addInboundEmailSender,
  deleteInboundEmailSender,
  getInboundEmail,
  listInboundEmailSenders,
} from '../../services/inboundEmailService';
import InboundConfirmationsInbox from '../../components/InboundConfirmationsInbox/InboundConfirmationsInbox';
import {
  isValidEmailAddress,
  normalizeEmailAddress,
} from '../../utils/emailAddress';
import styles from './Settings.module.css';

const AllowedSenders: React.FC = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [inboundAddress, setInboundAddress] = useState<string | null>(null);
  const [accountEmail, setAccountEmail] = useState<string | null>(null);
  const [senders, setSenders] = useState<InboundAlternateSender[]>([]);
  const [newEmail, setNewEmail] = useState('');
  const [addError, setAddError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [notProvisioned, setNotProvisioned] = useState(false);
  const [inboxOpen, setInboxOpen] = useState(false);

  const displayedAccountEmail =
    accountEmail || user?.email?.trim() || null;

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setNotProvisioned(false);
    try {
      const [emailInfo, sendersList] = await Promise.all([
        getInboundEmail().catch((err: unknown) => {
          if (err instanceof InboundEmailNotProvisionedError) return null;
          throw err;
        }),
        listInboundEmailSenders(),
      ]);
      setInboundAddress(emailInfo?.inboundEmailAddress ?? null);
      setAccountEmail(sendersList.accountEmail);
      setSenders(sendersList.alternates);
    } catch (err) {
      if (err instanceof InboundEmailNotProvisionedError) {
        setNotProvisioned(true);
        setSenders([]);
      } else {
        message.error(settingsEn.allowedSenders.loadFailed);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      navigate('/signin?redirect=/settings/allowed-senders', { replace: true });
      return;
    }
    void load();
  }, [authLoading, isAuthenticated, navigate, load]);

  const handleAdd = async (): Promise<void> => {
    setAddError(null);
    const trimmed = newEmail.trim();
    if (!isValidEmailAddress(trimmed)) {
      setAddError(settingsEn.allowedSenders.invalidEmail);
      return;
    }
    const normalized = normalizeEmailAddress(trimmed);
    if (senders.some((s) => s.email === normalized)) {
      setAddError(settingsEn.allowedSenders.duplicateEmail);
      return;
    }
    if (
      displayedAccountEmail &&
      normalizeEmailAddress(displayedAccountEmail) === normalized
    ) {
      setAddError(settingsEn.allowedSenders.accountAlreadyAllowed);
      return;
    }

    setAdding(true);
    try {
      await addInboundEmailSender(normalized);
      setNewEmail('');
      await load();
    } catch {
      setAddError(settingsEn.allowedSenders.addFailed);
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (sender: InboundAlternateSender): Promise<void> => {
    const key = sender.serverId ?? sender.email;
    setDeletingId(key);
    try {
      if (sender.serverId) {
        await deleteInboundEmailSender(sender.serverId);
      } else {
        // Fallback: no server id — refresh and hope list API catches up; local remove only.
        setSenders((prev) => prev.filter((s) => s.email !== sender.email));
        return;
      }
      await load();
    } catch (err) {
      if (err instanceof InboundEmailNotProvisionedError) {
        setSenders((prev) => prev.filter((s) => s.email !== sender.email));
      } else {
        message.error(settingsEn.allowedSenders.removeFailed);
      }
    } finally {
      setDeletingId(null);
    }
  };

  const explainer = inboundAddress
    ? settingsEn.allowedSenders.explainer(
        settingsEn.allowedSenders.appName,
        inboundAddress
      )
    : settingsEn.allowedSenders.explainerNoAddress(
        settingsEn.allowedSenders.appName
      );

  if (authLoading || (!isAuthenticated && loading)) {
    return (
      <div className={styles.settings}>
        <div className={styles.container}>
          <p className={styles.muted}>{settingsEn.allowedSenders.title}…</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.settings}>
      <div className={styles.container}>
        <header className={styles.header}>
          <Button
            type="link"
            className={styles.backLink}
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/settings', { replace: true })}
          >
            {settingsEn.back}
          </Button>
          <h1 className={styles.title}>{settingsEn.allowedSenders.title}</h1>
          <p className={styles.hint}>{explainer}</p>
          <Button
            icon={<MailOutlined />}
            onClick={() => setInboxOpen(true)}
            style={{ marginTop: 8 }}
          >
            {settingsEn.allowedSenders.openInbox}
          </Button>
        </header>

        {loading ? (
          <div className={styles.section}>
            <Spin />
          </div>
        ) : notProvisioned ? (
          <section className={styles.section}>
            <p className={styles.muted}>{settingsEn.allowedSenders.notProvisioned}</p>
          </section>
        ) : (
          <>
            {displayedAccountEmail ? (
              <section className={styles.section} aria-labelledby="account-email">
                <h2 id="account-email" className={styles.sectionTitle}>
                  {settingsEn.allowedSenders.accountEmail}
                </h2>
                <p className={styles.inboundAddress}>{displayedAccountEmail}</p>
              </section>
            ) : null}

            <section className={styles.section} aria-labelledby="allowed-senders">
              <h2 id="allowed-senders" className={styles.sectionTitle}>
                {settingsEn.allowedSenders.section}
              </h2>

              {senders.length === 0 ? (
                <p className={styles.muted}>{settingsEn.allowedSenders.empty}</p>
              ) : (
                <List
                  dataSource={senders}
                  renderItem={(sender) => {
                    const key = sender.serverId ?? sender.email;
                    return (
                      <List.Item
                        className={styles.templateRow}
                        actions={[
                          <Button
                            key="del"
                            type="text"
                            danger
                            icon={<DeleteOutlined />}
                            loading={deletingId === key}
                            disabled={deletingId === key}
                            aria-label={settingsEn.allowedSenders.remove}
                            onClick={() => void handleRemove(sender)}
                          />,
                        ]}
                      >
                        <span className={styles.inboundAddress}>{sender.email}</span>
                      </List.Item>
                    );
                  }}
                />
              )}

              <div className={styles.addRow}>
                <Input
                  value={newEmail}
                  disabled={adding}
                  placeholder={settingsEn.allowedSenders.addPlaceholder}
                  onChange={(e) => {
                    setNewEmail(e.target.value);
                    setAddError(null);
                  }}
                  onPressEnter={() => void handleAdd()}
                  aria-label={settingsEn.allowedSenders.addPlaceholder}
                  inputMode="email"
                  autoComplete="email"
                />
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  loading={adding}
                  disabled={adding || !newEmail.trim()}
                  onClick={() => void handleAdd()}
                >
                  {settingsEn.allowedSenders.add}
                </Button>
              </div>
              {addError ? <p className={styles.errorText}>{addError}</p> : null}
            </section>
          </>
        )}
      </div>

      <InboundConfirmationsInbox open={inboxOpen} onClose={() => setInboxOpen(false)} />
    </div>
  );
};

export default AllowedSenders;
